import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map, switchMap, timeout } from 'rxjs/operators';
import { ClientCaisseSyncService } from './client-caisse-sync.service';
import { Client, SoldeAvanceClientPayload, AvanceClient } from '../../models/client';
import { environment } from '../../../environments/environment';
import { PaginatedResponse } from '../../models/paginatedResponse';

/** Timeout HTTP clients (ms) — évite spinner infini si gateway/module-client bloque. */
const CLIENT_HTTP_TIMEOUT_MS = 20000;

@Injectable({
    providedIn: 'root',
})
export class ClientService {
    private apiUrl = `${environment.client_url}api`;

    constructor(
        private http: HttpClient,
        private readonly clientCaisseSync: ClientCaisseSyncService
    ) {}

    private withTimeout<T>(source: Observable<T>): Observable<T> {
        return source.pipe(
            timeout(CLIENT_HTTP_TIMEOUT_MS),
            catchError((err) => {
                if (err?.name === 'TimeoutError') {
                    return throwError(() => ({
                        status: 0,
                        message: 'Délai dépassé : le service clients ne répond pas (gateway/module-client).',
                        error: { errors: [{ message: 'Délai dépassé lors du chargement des clients (API).' }] }
                    }));
                }
                return throwError(() => err);
            })
        );
    }

    getActiveClient(stationId: number, page: number, limit: number, type?: string): Observable<PaginatedResponse<Client>> {
        let params = new HttpParams().set('page', String(page)).set('limit', String(limit));
        if (type) {
            params = params.set('type', type);
        }
        return this.withTimeout(
            this.http.get<PaginatedResponse<Client>>(`${this.apiUrl}/clients/station/${stationId}`, { params })
        );
    }

    getArchivedClient(stationId: number, page: number, limit: number): Observable<PaginatedResponse<Client>> {
        return this.withTimeout(
            this.http.get<PaginatedResponse<Client>>(
                `${this.apiUrl}/clients/station/${stationId}/archive?page=${page}&limit=${limit}`
            )
        );
    }

    getClientById(clientId: number | string): Observable<Client> {
       // console.log('ClientService: Fetching client with ID=', clientId);
        return this.http.get<Client>(`${this.apiUrl}/clients/${clientId}`);
    }

    addClient(client: Client): Observable<Client> {
        const formPayload = client as unknown as Record<string, unknown>;
        return this.http.post<Client>(`${this.apiUrl}/clients`, client).pipe(
            switchMap((erpResponse) =>
                this.clientCaisseSync.syncAfterErpCreate(formPayload, erpResponse).pipe(
                    map(() => erpResponse)
                )
            )
        );
    }

    updateClient(clientId: number, client: Partial<Client>): Observable<Client> {
        const formPayload = client as unknown as Record<string, unknown>;
        return this.http.put<Client>(`${this.apiUrl}/clients/${clientId}`, client).pipe(
            switchMap((erpResponse) =>
                this.clientCaisseSync.syncAfterErpUpdate(formPayload, erpResponse).pipe(
                    map(() => erpResponse)
                )
            )
        );
    }

    archiveClient(clientId: number, is_archive: boolean): Observable<Client> {
       // console.log(`archiveClient Request: ${this.apiUrl}/clients/${clientId}/archive`, { is_archive });
        return this.http.patch<Client>(`${this.apiUrl}/clients/${clientId}/archive`, { is_archive });
    }

    /**
     * Envoyer un email à un client
     * @param clientId ID du client
     * @param emailData Données de l'email (to, cc, subject, message, user_id)
     */
    sendEmail(clientId: number, emailData: { to: string; cc?: string; subject: string; message: string; user_id: number }): Observable<any> {
        console.log('📧 [ClientService.sendEmail] Envoi email pour client:', clientId);
        console.log('📧 [ClientService.sendEmail] Données email:', emailData);
        return this.http.post(`${this.apiUrl}/clients/${clientId}/send-email`, emailData);
    }

    /**
     * POST /api/clients/solde-avance-client – enregistre un solde d'avance client.
     * Body : client_id, station_id, solde_avance, note_avance
     */
    createSoldeAvanceClient(data: SoldeAvanceClientPayload): Observable<any> {
        return this.http.post(`${this.apiUrl}/clients/solde-avance-client`, data);
    }

    /**
     * GET /api/clients/avances/client/:client_id/station/:station_id
     * Query optionnelle : user_id (souvent obligatoire côté API).
     */
    getAvancesClient(clientId: number, stationId: number, userId?: number): Observable<AvanceClient[] | any> {
        let params = new HttpParams();
        if (userId != null && userId > 0) {
            params = params.set('user_id', String(userId));
        }
        const url = `${this.apiUrl}/clients/avances/client/${clientId}/station/${stationId}`;
        const opts = params.keys().length ? { params } : {};
        return this.http.get<AvanceClient[] | any>(url, opts);
    }
}
